import { Component, inject, Input, OnChanges } from '@angular/core';
import { ApexOptions, ChartType, NgApexchartsModule } from 'ng-apexcharts';
import { FormsModule } from '@angular/forms';
import { TitleCasePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'app-chart-card',
  standalone: true,
  imports: [NgApexchartsModule, FormsModule, TranslatePipe, NgSelectModule],
  templateUrl: './chart-card.component.html',
  styleUrls: ['./chart-card.component.scss'],
})
export class ChartCardComponent implements OnChanges {
  @Input() data: number[] = [];
  @Input() labels: string[] = [];
  @Input() title: string = 'Dynamic Chart';
  @Input() defaultType: ChartType = 'donut';
  @Input() series: { name: string; data: number[] }[] | null = null;
  @Input() distributed: boolean = true;
  @Input() showTypeSwitcher: boolean = false;
  chartTypes: ChartType[] = ['donut', 'bar'];
  chartType: ChartType = 'bar';
  public chartOptions: Partial<ApexOptions> = {};
  private translate = inject(TranslateService);

  ngOnChanges(): void {
    this.chartType = this.defaultType;
    this.updateChartOptions();
  }

  onTypeChange(newType: ChartType): void {
    this.chartType = newType;
    this.updateChartOptions();
  }

  private updateChartOptions(): void {
    const commonOptions = {
      chart: {
        type: this.chartType,
        height: 350,
        toolbar: {
          show: false,
        },
      },
      title: {
        text: this.title,
        align: 'left' as const,
        style: {
          fontSize: '18px',
          fontWeight: '600',
          fontFamily: '"Titillium Web", sans-serif',
          color: '#374151',
        },
      },
    };

    if (this.chartType === 'donut') {
      this.chartOptions = {
        ...commonOptions,
        series: [...this.data],
        labels: [...this.labels],
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontFamily: 'Titillium Web, sans-serif',
                  fontWeight: 600,
                  color: '#374151',
                },
                value: {
                  show: true,
                  fontSize: '20px',
                  fontFamily: 'Titillium Web, sans-serif',
                  fontWeight: 700,
                  color: '#111827',
                  formatter: (val: string) => val,
                },
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '16px',
                  fontFamily: 'Titillium Web, sans-serif',
                  fontWeight: 600,
                  color: '#6B7280',
                  formatter: () =>
                    this.data.reduce((a, b) => a + b, 0).toString(),
                },
              },
            },
          },
        },
      };
    } else {
      this.chartOptions = {
        ...commonOptions,
        plotOptions: {
          bar: {
            distributed: this.distributed,
            columnWidth: '90%',
            borderRadius: 4,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: 10,
          formatter: (val: number) => {
            return val === 0 ? '' : val.toString();
          },
          style: {
            fontSize: '12px',
            fontFamily: '"Titillium Web", sans-serif',
            fontWeight: '600',
            colors: ['#ffffff'],
          },
          background: {
            enabled: true,
            foreColor: '#949494',
            borderRadius: 4,
            padding: 1,
            opacity: 100,
            borderWidth: 1,
            borderColor: '#fff',
          },
        },
        yaxis: {
          decimalsInFloat: 0,
          labels: {
            style: {
              fontSize: '12px',
              fontFamily: '"Titillium Web", sans-serif',
              fontWeight: '500',
              colors: ['#6B7280'],
            },
            formatter: (value: number) => value.toFixed(0),
          },
        },
        xaxis: {
          categories: [...this.labels],
          labels: {
            style: {
              fontSize: '12px',
              fontFamily: '"Titillium Web", sans-serif',
              fontWeight: '500',
              colors: '#6B7280',
            },
            formatter: (value: string) => {
              if (
                value.includes('yyyy') ||
                /^[A-Za-z]{3}\s\d{4}$/.test(value)
              ) {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  const lang = this.translate.currentLang || 'en';
                  return new Intl.DateTimeFormat(lang, {
                    month: 'short',
                  }).format(date);
                }
              }
              return value;
            },
          },
        },
        legend: {
          fontSize: '14px',
          fontFamily: '"Titillium Web", sans-serif',
          fontWeight: 500,
          labels: {
            colors: ['#374151'],
            useSeriesColors: false,
          },
        },
        series: this.series ?? [
          {
            name: 'Files',
            data: [...this.data],
          },
        ],
        labels: [],
      };
    }
  }
}
